import React, { useEffect, useRef, useState } from 'react';

export default function FinanceEmergencyControl({ token, active }) {
  const [control, setControl] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const generation = useRef(0);
  const endpoint = `${process.env.REACT_APP_API_URL || '/api'}/admin/finance-control`;
  useEffect(() => {
    if (!active || !token) return undefined;
    let disposed = false;
    const version = ++generation.current;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    setControl(null); setError('');
    fetch(endpoint, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error();
        const value = await response.json();
        if (typeof value.paused !== 'boolean') throw new Error();
        if (value.review && (!Array.isArray(value.review.items) || value.review.items.length > 300
          || !Number.isSafeInteger(value.review.total) || value.review.total < value.review.items.length
          || !value.review.items.every(item => typeof item.reference === 'string'
            && typeof item.source === 'string' && typeof item.needs_review === 'boolean'))) throw new Error();
        if (!disposed && generation.current === version) setControl(value);
      }).catch(() => { if (!disposed && generation.current === version) setError('Status nicht verfügbar. Ein sicherer Rückwechsel ist damit nicht bestätigt.'); })
      .finally(() => clearTimeout(timeout));
    return () => { disposed = true; clearTimeout(timeout); controller.abort(); };
  }, [active, token, endpoint, refresh]);
  async function pause() {
    if (busy) return;
    ++generation.current; // A late status read must not overwrite a confirmed pause.
    setBusy(true); setError('');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${endpoint}/pause`, { method: 'POST',
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
      const value = await response.json();
      if (!response.ok || value.paused !== true) throw new Error();
      setControl(value);
    } catch (_) { setControl(null); setError('Not-Aus nicht bestätigt. Status neu prüfen und bei Bedarf technisch eingreifen.'); }
    finally { clearTimeout(timeout); setBusy(false); }
  }
  if (!active) return null;
  return <section className="card" aria-labelledby="finance-emergency-title">
    <h3 id="finance-emergency-title">Finanzverarbeitung – Not-Aus</h3>
    <p role="status">{control ? (control.paused ? 'Neue Hub-Finanzaktionen sind angehalten.' : 'Die neue Hub-Finanzverarbeitung ist freigegeben.') : 'Status nicht bestätigt.'}</p>
    <p>Stoppt neue Aktionen der neuen Finanzverarbeitung. Bereits laufende Anbieteranfragen können noch abgeschlossen werden. Zahlungen der Kunden und bestehende Abos werden dadurch nicht gekündigt.</p>
    <button type="button" className="danger-button" disabled={busy || control?.paused === true} onClick={pause}>
      {busy ? 'Wird angehalten …' : 'Neue Finanzaktionen jetzt anhalten'}
    </button>
    <button type="button" disabled={busy} onClick={() => setRefresh(value => value + 1)}>Finanzstatus prüfen</button>
    {error && <p role="alert">{error}</p>}
    {control?.review && <div aria-label="Finanzprüffälle">
      <h4>Offene Finanzvorgänge ({control.review.total})</h4>
      <p>Ein Vorgang mit unbekanntem Ergebnis wird nicht automatisch wiederholt. Erst den tatsächlichen Anbieterstand abgleichen.</p>
      {control.review.items.map(item => <article key={`${item.source}:${item.reference}`}>
        <p><code>{item.reference}</code>: {item.needs_review ? 'Prüfung erforderlich – nicht erneut auslösen' : 'Bearbeitung vorgemerkt oder laufend'}</p>
      </article>)}
      {control.review.truncated && <p role="alert">Die Liste ist begrenzt. Weitere offene Vorgänge müssen technisch geprüft werden.</p>}
      {!control.review.total && <p>Keine offenen Vorgänge in dieser Prüfliste. Das allein bestätigt noch keinen vollständigen Anbieterabgleich.</p>}
    </div>}
    <p>sevify und MiracleSync werden nicht automatisch eingeschaltet. Vor dem Rückwechsel laufende und unklare Vorgänge sowie bereits ausgestellte Belege abgleichen – sonst drohen Doppelbelege oder Doppelbuchungen.</p>
  </section>;
}
